import { useEffect, useState, useRef } from 'react';
import { listarCerts, uploadCert, revogarCert, certAudit } from '../api';
import { PageHeader, Loading, Empty, Alert, StatusBadge } from '../components/common';
import { fmtCnpj, fmtData, fmtDataHora } from '../lib/format';

export default function Certificados({ clienteId }) {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [audit, setAudit] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const fileRef = useRef();
  const senhaRef = useRef();

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    listarCerts(clienteId)
      .then(r => setCerts(r.items || []))
      .catch(err => setMsg({ tipo: 'danger', txt: err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [clienteId]);

  const fazerUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    const senha = senhaRef.current?.value;
    if (!file || !senha) return setMsg({ tipo: 'warning', txt: 'Selecione .pfx e digite a senha' });

    setEnviando(true);
    try {
      const r = await uploadCert(clienteId, file, senha);
      setMsg({ tipo: 'success', txt: `Certificado ${r.titular_nome} carregado · vence em ${fmtData(r.nao_depois)}` });
      fileRef.current.value = '';
      senhaRef.current.value = '';
      carregar();
    } catch (err) {
      setMsg({ tipo: 'danger', txt: err.message });
    } finally {
      setEnviando(false);
    }
  };

  const revogar = async (id) => {
    const motivo = prompt('Motivo da revogação:');
    if (!motivo || motivo.length < 5) return;
    try {
      await revogarCert(clienteId, id, motivo);
      setMsg({ tipo: 'success', txt: `Certificado #${id} revogado` });
      carregar();
    } catch (err) {
      setMsg({ tipo: 'danger', txt: err.message });
    }
  };

  const verAudit = async (id) => {
    try {
      const r = await certAudit(clienteId, id);
      setAudit({ certId: id, items: r.items || [] });
    } catch (err) {
      setMsg({ tipo: 'danger', txt: err.message });
    }
  };

  if (!clienteId) return <Empty title="Selecione um cliente" />;

  return (
    <>
      <PageHeader title="Certificados digitais" sub="A1 (.pfx) — guardado criptografado com AES-256-GCM" />

      {msg && <Alert tipo={msg.tipo}>{msg.txt}</Alert>}

      <div className="card mb-2">
        <div className="card__title">Carregar novo certificado</div>
        <form onSubmit={fazerUpload} className="flex" style={{ gap: 8 }}>
          <input ref={fileRef} type="file" accept=".pfx,.p12" required />
          <input ref={senhaRef} type="password" placeholder="Senha do .pfx" style={{ maxWidth: 200 }} required />
          <button className="primary" type="submit" disabled={enviando}>
            {enviando ? 'Enviando…' : '↑ Upload'}
          </button>
        </form>
      </div>

      <div className="card">
        {loading ? <Loading /> : certs.length === 0 ? (
          <Empty title="Sem certificados" sub="Faça upload de um .pfx pra começar." />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Titular</th>
                <th>CNPJ</th>
                <th>Emissor</th>
                <th>Validade</th>
                <th>Status</th>
                <th>Usos</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {certs.map(c => (
                <tr key={c.id}>
                  <td>{c.titular_nome || '—'}</td>
                  <td className="small">{fmtCnpj(c.titular_cnpj)}</td>
                  <td className="small">{c.emissor || '—'}</td>
                  <td>{fmtData(c.nao_depois)}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td className="num">{c.total_usos}</td>
                  <td className="flex">
                    <button className="ghost" onClick={() => verAudit(c.id)}>Audit</button>
                    {c.status === 'ativo' && (
                      <button className="ghost danger" onClick={() => revogar(c.id)}>Revogar</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {audit && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
        }} onClick={() => setAudit(null)}>
          <div className="card" style={{ width: 720, maxHeight: '85vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="flex mb-2">
              <h2 style={{ margin: 0, fontSize: 16 }}>Audit do cert #{audit.certId}</h2>
              <div className="toolbar__spacer" />
              <button onClick={() => setAudit(null)}>Fechar</button>
            </div>
            {audit.items.length === 0 ? <Empty title="Sem registros" /> : (
              <table className="data">
                <thead>
                  <tr><th>Quando</th><th>Ação</th><th>Serviço</th><th>Sucesso</th><th>Detalhe</th></tr>
                </thead>
                <tbody>
                  {audit.items.map(a => (
                    <tr key={a.id}>
                      <td className="small">{fmtDataHora(a.ocorreu_em)}</td>
                      <td><span className="badge badge--neutral">{a.acao}</span></td>
                      <td className="small">{a.servico || a.usuario || '—'}</td>
                      <td>{a.sucesso ? '✓' : '✗'}</td>
                      <td className="small muted">{a.detalhe || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}
